import React, { useEffect, useState, useRef  } from 'react';
import { formatDateToSydney } from "./utils/dateUtils"; // Import the function

const TopItems = () => {
  const [items, setItems] = useState([]);
  const [soundEnabled, setSoundEnabled] = useState(true); // Track sound state
  const prevItemsRef = useRef([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState("");
  const [filterText, setFilterText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 20;

  const playSound = () => {
    const audio = new Audio("/notification.mp3"); 
    audio.play();
  };

  const getSydneyTime = () => {
    const sydneyTime = new Date().toLocaleString("en-US", { timeZone: "Australia/Sydney", hour12: true });
    return new Date(sydneyTime).toLocaleString("en-US", {
      weekday: "short", // Optional: e.g., "Mon"
      year: "numeric",
      month: "short", // Optional: e.g., "Mar"
      day: "numeric",
      hour: "2-digit", // Ensures two-digit hour
      minute: "2-digit", // Ensures two-digit minute
      second: "2-digit", // Ensures two-digit second
      hour12: true // Show AM/PM
    });
  };


  // Fetch items from the API
  const fetchItems = async (isScheduled = false) => {
    try {
      const API_URL = process.env.REACT_APP_API_URL;
      const requestOptions = {
        method: "GET",
        redirect: "follow"
      };      
      const response = await fetch(API_URL, requestOptions);
      console.log("API Response:", response)
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();

      if(isScheduled && soundEnabled) {
        const prevItems = prevItemsRef.current;
        const itemsChanged = JSON.stringify(prevItems) !== JSON.stringify(data);
        if(itemsChanged) {
          playSound();
        }
      }

      setItems(data);
      setLastUpdated(getSydneyTime());
      prevItemsRef.current = data;
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems(); // Initial fetch
    // Set interval to refresh data every 60 seconds
    const interval = setInterval(() => {
      // Get the current time in Sydney
      const sydneyTime = new Date().toLocaleString("en-US", { timeZone: "Australia/Sydney" });
      const currentTime = new Date(sydneyTime);
      // Get the hour and minute
      const hour = currentTime.getHours();
      const minute = currentTime.getMinutes();      
      // Check if current time is between 6:30 AM and 7:00 PM
      if ((hour > 6 || (hour === 6 && minute >= 30)) && (hour < 19 || (hour === 19 && minute === 0))) {
        fetchItems(true);
      } else {
        console.log("Fetch request is outside of the allowed time range.");
      }
    }, 20000); // 20 second refresh

    return () => clearInterval(interval); // Cleanup on component unmount
  }, []);

  if (loading) {
    return (
      <div className="container">
        <p className="loading-state">Loading headlines...</p>
      </div>
    );
  }

  const normalizedFilter = filterText.trim().toLowerCase();
  const filteredItems = normalizedFilter
    ? items.filter((item) =>
        [item.title, item.desc, item.source]
          .some((field) => field && field.toLowerCase().includes(normalizedFilter))
      )
    : items;

  const totalPages = Math.max(1, Math.ceil(filteredItems.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const pageStart = (safePage - 1) * PAGE_SIZE;
  const pagedItems = filteredItems.slice(pageStart, pageStart + PAGE_SIZE);

  const handleFilterChange = (e) => {
    setFilterText(e.target.value);
    setCurrentPage(1);
  };

  const handleClick = () => {
    // Toggle the soundEnabled state
    setSoundEnabled((prev) => {
      const newState = !prev;
      
      // If sound is enabled, play the sound
      if (newState) {
        playSound()
      }

      return newState;
    });
  };

  return (
    <div className="container">
      <header className="app-header">
        <h1 className="header-title">ASXPulse</h1>
        <p className="header-subtitle">Your Live Beat on the ASX</p>
      </header>

      <div className="toolbar">
        <input
          type="text"
          className="filter-input"
          placeholder="Filter by code, title or source..."
          value={filterText}
          onChange={handleFilterChange}
        />
        <button onClick={handleClick} className="sound-button" aria-pressed={soundEnabled}>
          {soundEnabled ? "🔊 Sound on" : "🔇 Sound off"}
        </button>
      </div>

      <div className="status-bar">
        <span className="last-updated">Last updated: {lastUpdated}</span>
        {normalizedFilter && (
          <span className="filter-count">
            Showing {filteredItems.length} of {items.length} headlines
          </span>
        )}
      </div>

      {filteredItems.length === 0 ? (
        <p className="empty-state">
          {normalizedFilter
            ? "No headlines match your filter."
            : "No headlines yet — check back shortly."}
        </p>
      ) : (
        <>
          <div className="card-list">
            {pagedItems.map((item) => (
              <article className="card" key={`${item.source}-${item.detectedTimestamp}-${item.title}`}>
                <div className="card-meta">
                  <span className="badge">{item.source}</span>
                  <time className="card-time">{formatDateToSydney(item.detectedTimestamp)}</time>
                </div>
                <h2 className="card-title">{item.title}</h2>
                <p className="card-desc">{item.desc}</p>
              </article>
            ))}
          </div>

          {totalPages > 1 && (
            <nav className="pagination" aria-label="Headline pages">
              <button
                type="button"
                className="pagination-button"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={safePage === 1}
              >
                ← Prev
              </button>
              <span className="pagination-status">
                Page {safePage} of {totalPages}
              </span>
              <button
                type="button"
                className="pagination-button"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={safePage === totalPages}
              >
                Next →
              </button>
            </nav>
          )}
        </>
      )}
    </div>
  );
};

export default TopItems;
